import { useEffect, useState } from "react";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { GitCompare, ArrowRight, TrendingUp, TrendingDown } from "lucide-react";

const ESTADO = {
  nuevo: { label: "Nuevo", cls: "text-emerald-500" },
  eliminado: { label: "Eliminado", cls: "text-destructive" },
  modificado: { label: "Modificado", cls: "text-amber-500" },
  igual: { label: "Igual", cls: "text-muted-foreground" },
};

export default function ComparadorView() {
  const [list, setList] = useState([]);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/presupuestos").then(({ data }) => setList(data));
  }, []);

  const compare = async () => {
    if (!a || !b) { toast.error("Selecciona dos presupuestos"); return; }
    if (a === b) { toast.error("Selecciona presupuestos distintos"); return; }
    try {
      const { data } = await api.get("/comparar", { params: { a, b } });
      setResult(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deltaCls = (v) => (v > 0.009 ? "text-destructive" : v < -0.009 ? "text-emerald-500" : "text-muted-foreground");

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="h-12 shrink-0 border-b border-border flex items-center px-3 gap-2 flex-wrap">
        <GitCompare className="h-4 w-4 text-primary" />
        <h2 className="font-head font-bold">Comparador de Presupuestos</h2>
        <div className="flex-1" />
        <Select value={a} onValueChange={setA}>
          <SelectTrigger className="h-8 w-56 rounded-sm text-xs" data-testid="compare-select-a"><SelectValue placeholder="Versión A (base)" /></SelectTrigger>
          <SelectContent>{list.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
        </Select>
        <ArrowRight className="h-4 w-4 text-muted-foreground" />
        <Select value={b} onValueChange={setB}>
          <SelectTrigger className="h-8 w-56 rounded-sm text-xs" data-testid="compare-select-b"><SelectValue placeholder="Versión B (comparar)" /></SelectTrigger>
          <SelectContent>{list.map((p) => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}</SelectContent>
        </Select>
        <Button size="sm" className="h-8 rounded-sm" onClick={compare} data-testid="compare-btn">Comparar</Button>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {!result ? (
          <div className="text-muted-foreground text-sm">Selecciona dos presupuestos y presiona Comparar.</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4 max-w-3xl">
              <div className="border border-border rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">A · {result.a.nombre}</div>
                <div className="font-mono-num text-lg mt-1">$ {fmt(result.a.total, 2)}</div>
              </div>
              <div className="border border-border rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">B · {result.b.nombre}</div>
                <div className="font-mono-num text-lg mt-1">$ {fmt(result.b.total, 2)}</div>
              </div>
              <div className="border border-primary/40 rounded-sm p-3 bg-primary/5">
                <div className="text-[10px] uppercase tracking-wide text-primary flex items-center gap-1">
                  {result.delta_total >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />} Diferencia total
                </div>
                <div className={`font-mono-num text-lg mt-1 ${deltaCls(result.delta_total)}`} data-testid="compare-delta-total">
                  {result.delta_total >= 0 ? "+" : ""}$ {fmt(result.delta_total, 2)}
                </div>
              </div>
            </div>

            <div className="border border-border rounded-sm overflow-hidden">
              <table className="qc-table">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Código</th>
                    <th>Concepto</th>
                    <th style={{ width: 100 }} className="text-right">P.U. A</th>
                    <th style={{ width: 100 }} className="text-right">P.U. B</th>
                    <th style={{ width: 110 }} className="text-right">Δ P.U.</th>
                    <th style={{ width: 120 }} className="text-right">Importe A</th>
                    <th style={{ width: 120 }} className="text-right">Importe B</th>
                    <th style={{ width: 120 }} className="text-right">Δ Importe</th>
                    <th style={{ width: 90 }}>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {result.filas.map((f, i) => (
                    <tr key={i} data-testid={`compare-row-${f.codigo}`}>
                      <td className="font-mono-num text-primary text-xs">{f.codigo}</td>
                      <td className="text-xs">{f.descripcion}</td>
                      <td className="text-right font-mono-num text-xs">{f.pu_a == null ? "—" : fmt(f.pu_a, 2)}</td>
                      <td className="text-right font-mono-num text-xs">{f.pu_b == null ? "—" : fmt(f.pu_b, 2)}</td>
                      <td className={`text-right font-mono-num text-xs ${deltaCls(f.delta_pu)}`}>{f.delta_pu >= 0 ? "+" : ""}{fmt(f.delta_pu, 2)}</td>
                      <td className="text-right font-mono-num">{fmt(f.importe_a, 2)}</td>
                      <td className="text-right font-mono-num">{fmt(f.importe_b, 2)}</td>
                      <td className={`text-right font-mono-num ${deltaCls(f.delta_importe)}`}>{f.delta_importe >= 0 ? "+" : ""}{fmt(f.delta_importe, 2)}</td>
                      <td className={`text-xs ${ESTADO[f.estado]?.cls}`}>{ESTADO[f.estado]?.label}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
